import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle2, ChevronDown, Check, LogOut, Settings, Loader2 } from "lucide-react";
import { cn } from "../../utils/cn";
import { useProfileMenu } from "../../hooks/use-profile-menu";

const toLabel = (str) => {
  if (str === "candidate") return "Marketing Executive";
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function ProfileDropdown({ defaultRole = "customer", profileRoute = "/customer/profile" }) {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  
  const {
    user,
    openMenu,
    setOpenMenu,
    switchingRole,
    meJobLoading,
    displayName,
    activeRole,
    availableRoles,
    registrableRoles,
    meAppStatus,
    hmAppStatus,
    handleLogout,
    handleBecomeMarketing,
    handleSwitchRole,
  } = useProfileMenu(defaultRole);

  useEffect(() => {
    const onDown = (e) => {
      if (!openMenu) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenMenu(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openMenu, setOpenMenu]);

  const userInitials = displayName !== "Account" ? displayName.split(" ").map((n) => n[0]).join("").substring(0, 2).toUpperCase() : "U";

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpenMenu((v) => !v)}
        className={cn(
          "inline-flex items-center gap-2.5 rounded-full pl-1.5 pr-3 py-1.5",
          "font-semibold text-brand-text bg-white border border-slate-200 shadow-sm",
          "hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300 transition-all duration-300",
          "focus:outline-none focus:ring-2 focus:ring-brand-primary/40",
        )}
        aria-haspopup="menu"
        aria-expanded={openMenu}
      >
        {user?.profilePicture || user?.avatar || user?.image ? (
          <img 
            src={user.profilePicture || user.avatar || user.image} 
            alt={displayName} 
            className="h-8 w-8 rounded-full object-cover shadow-sm border border-slate-100"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-xs font-bold border border-brand-primary/20">
            {userInitials}
          </div>
        )}
        <div className="hidden sm:flex items-center gap-2">
          <span className="text-sm">{displayName}</span>
          <span className="flex items-center text-[10px] uppercase tracking-wider bg-green-50 text-green-600 px-1.5 py-0.5 rounded-sm font-bold">
            <Check className="w-3 h-3 mr-0.5" /> Verified
          </span>
        </div>
        <ChevronDown className={cn("h-4 w-4 text-brand-muted transition-transform duration-300 ml-1", openMenu && "rotate-180")} />
      </button>

      {openMenu && (
        <div
          role="menu"
          className={cn(
            "absolute right-0 mt-2 w-64 rounded-xl border bg-white shadow-xl z-[9999]",
            "border-gray-200 dark:border-slate-700 dark:bg-slate-900",
          )}
        >
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{displayName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{toLabel(activeRole)}</p>
          </div>

          {/* Profile */}
          <div className="px-2 pt-2">
            <button
              type="button"
              onClick={() => { setOpenMenu(false); navigate("/account"); }}
              className={cn(
                "w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                "text-gray-800 dark:text-gray-100 hover:bg-[#FFF8EF] hover:text-[#EFA938] dark:hover:bg-slate-800",
              )}
              role="menuitem"
            >
              <Settings className="h-4 w-4 text-[#EFA938]" />
              Profile
            </button>
          </div>

          {/* Switch Role */}
          {availableRoles.length > 1 && (
            <div className="px-2 pt-3">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Switch Role
              </p>
              <div className="space-y-0.5">
                {availableRoles.map((role) => {
                  const isActive = role === activeRole;
                  const isSwitching = switchingRole === role;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleSwitchRole(role)}
                      disabled={isSwitching}
                      className={cn(
                        "w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "text-[#EFA938] font-semibold"
                          : "text-gray-700 dark:text-gray-300 hover:bg-[#FFF8EF] hover:text-[#EFA938] dark:hover:bg-slate-800",
                        isSwitching && "opacity-60 cursor-wait",
                      )}
                      role="menuitem"
                    >
                      <span>{toLabel(role)}</span>
                      {isSwitching
                        ? <Loader2 className="h-4 w-4 animate-spin text-[#EFA938]" />
                        : isActive && <Check className="h-4 w-4 text-[#EFA938]" />
                      }
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Register More Roles */}
          {registrableRoles.length > 0 && (
            <div className="px-2 pt-3">
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Register More Roles
              </p>
              <div className="space-y-0.5">
                {registrableRoles.map((role) => {
                  const Icon = role.icon;
                  const isPending =
                    (role.key === "marketing_executive" && meAppStatus === "pending") ||
                    (role.key === "handyman" && hmAppStatus === "pending");
                  const isRejected =
                    (role.key === "marketing_executive" && meAppStatus === "rejected") ||
                    (role.key === "handyman" && hmAppStatus === "rejected");
                  return (
                    <button
                      key={role.key}
                      type="button"
                      disabled={isPending || (role.key === "marketing_executive" && meJobLoading)}
                      onClick={() => {
                        if (isPending) return;
                        if (role.key === "marketing_executive") {
                          handleBecomeMarketing();
                        } else {
                          setOpenMenu(false);
                          navigate(role.route);
                        }
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm",
                        isPending
                          ? "text-yellow-600 dark:text-yellow-400 cursor-not-allowed opacity-70"
                          : isRejected
                          ? "text-red-500 dark:text-red-400 hover:bg-[#FFF8EF] dark:hover:bg-slate-800 transition-colors"
                          : "text-gray-700 dark:text-gray-300 hover:bg-[#FFF8EF] hover:text-[#EFA938] dark:hover:bg-slate-800 transition-colors",
                        role.key === "marketing_executive" && meJobLoading && "opacity-60 cursor-wait",
                      )}
                      role="menuitem"
                    >
                      {role.key === "marketing_executive" && meJobLoading
                        ? <Loader2 className="h-4 w-4 animate-spin text-[#EFA938]" />
                        : <Icon className={cn("h-4 w-4", isPending ? "text-yellow-500" : role.iconColor)} />
                      }
                      {isPending
                        ? `${role.label} (Pending)`
                        : isRejected
                        ? `${role.label} (Rejected — Reapply)`
                        : role.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Logout */}
          <div className="px-2 pt-2 pb-2 mt-1 border-t border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={() => { setOpenMenu(false); handleLogout(); }}
              className={cn(
                "w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                "text-[#EFA938] hover:bg-[#FFF8EF] dark:hover:bg-slate-800 transition-colors",
              )}
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
